import { useState, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import { MdConfirmationNumber, MdAutorenew } from 'react-icons/md'
import { useAuth } from '@/store/hooks'
import {
    getCoupon,
    createCoupon,
    updateCoupon,
    generateCouponCode,
    Coupon,
} from '@/lib/api/commerce/coupons'
import { getCatalogsByCompany, catalogBasePrice } from '@/lib/api/catalog/catalog-api'
import { getStoreConfigByCompany, StoreConfig } from '@/lib/api/commerce/store-config'
import { apiRequest } from '@/lib/api/request'

interface CategoryItem {
    id: number
    name: string
    parentId?: number | null
    children?: CategoryItem[]
}

interface ProductItem {
    id: number
    name: string
    photo?: string
    price: number
}

export default function CouponsFormPage() {
    const { user } = useAuth()
    const navigate = useNavigate()
    const { id } = useParams<{ id: string }>()
    const isEditing = !!id
    const companyId = user?.companyId

    const [loading, setLoading] = useState(false)
    const [loadingData, setLoadingData] = useState(false)
    const [generatingCode, setGeneratingCode] = useState(false)
    const [coupon, setCoupon] = useState<Coupon | null>(null)

    // Form fields
    const [code, setCode] = useState('')
    const [name, setName] = useState('')
    const [description, setDescription] = useState('')
    const [discountType, setDiscountType] = useState<'PERCENTAGE' | 'FIXED_AMOUNT'>('PERCENTAGE')
    const [discountValue, setDiscountValue] = useState('')
    const [startDate, setStartDate] = useState('')
    const [endDate, setEndDate] = useState('')
    const [maxUses, setMaxUses] = useState('')
    const [maxUsesPerClient, setMaxUsesPerClient] = useState('')
    const [minimumPurchase, setMinimumPurchase] = useState('')
    const [active, setActive] = useState(true)

    // Filters
    const [showFilters, setShowFilters] = useState(false)
    const [categories, setCategories] = useState<CategoryItem[]>([])
    const [subcategories, setSubcategories] = useState<CategoryItem[]>([])
    const [selectedCategoryId, setSelectedCategoryId] = useState<number | ''>('')
    const [selectedSubcategoryId, setSelectedSubcategoryId] = useState<number | ''>('')
    const [products, setProducts] = useState<ProductItem[]>([])
    const [selectedProductIds, setSelectedProductIds] = useState<number[]>([])
    const [productSearch, setProductSearch] = useState('')
    const [availableStores, setAvailableStores] = useState<StoreConfig[]>([])
    const [selectedStoreIds, setSelectedStoreIds] = useState<number[]>([])

    useEffect(() => {
        if (!companyId) return
        loadCategories()
        loadProducts()
        loadStores()
        if (isEditing && id) {
            loadCoupon(parseInt(id))
        }
    }, [companyId, id])

    useEffect(() => {
        if (selectedCategoryId) {
            const cat = categories.find((c) => c.id === selectedCategoryId)
            setSubcategories(cat?.children || [])
            setSelectedSubcategoryId('')
        } else {
            setSubcategories([])
            setSelectedSubcategoryId('')
        }
    }, [selectedCategoryId, categories])

    async function loadCategories() {
        try {
            const response = await apiRequest('product-categories/company/' + companyId, {})
            if (response?.success) {
                setCategories(response.result as any)
            }
        } catch {
            // silent
        }
    }

    async function loadStores() {
        try {
            const response = await getStoreConfigByCompany(companyId!)
            if (response?.success) {
                const stores = Array.isArray(response.result) ? response.result : [response.result]
                setAvailableStores(stores.filter(Boolean))
            }
        } catch {}
    }

    async function loadProducts() {
        try {
            // Mercadoria é item de catálogo (`category = PRODUCT`) desde a unificação.
            const response = await getCatalogsByCompany(companyId!, {
                category: 'PRODUCT',
                activeOnly: true,
                isListing: true,
                limit: 200,
            })
            if (response?.success) {
                setProducts(
                    (response.result || []).map((p: any) => ({
                        id: p.id,
                        name: p.name,
                        photo: p.media?.[0]?.url ?? null,
                        price: catalogBasePrice(p, companyId!),
                    }))
                )
            }
        } catch {
            // silent
        }
    }

    async function loadCoupon(couponId: number) {
        setLoadingData(true)
        try {
            const response = await getCoupon(couponId)
            if (response?.success) {
                const c = response.result
                setCoupon(c)
                setCode(c.code)
                setName(c.name || '')
                setDescription(c.description || '')
                setDiscountType(c.discountType)
                setDiscountValue(
                    c.discountType === 'PERCENTAGE'
                        ? String(c.discountValue)
                        : String(c.discountValue / 100)
                )
                setStartDate(c.startDate ? c.startDate.substring(0, 10) : '')
                setEndDate(c.endDate ? c.endDate.substring(0, 10) : '')
                setMaxUses(c.maxUses ? String(c.maxUses) : '')
                setMaxUsesPerClient(c.maxUsesPerClient ? String(c.maxUsesPerClient) : '')
                setMinimumPurchase(c.minimumPurchase ? String(c.minimumPurchase / 100) : '')
                setActive(c.active)

                if (c.categoryId) {
                    setSelectedCategoryId(c.categoryId)
                    setShowFilters(true)
                }
                if (c.subcategoryId) {
                    setSelectedSubcategoryId(c.subcategoryId)
                    setShowFilters(true)
                }
                if (c.products && c.products.length > 0) {
                    setSelectedProductIds(c.products.map((p: any) => p.catalogId))
                    setShowFilters(true)
                }
                if (c.stores && c.stores.length > 0) {
                    setSelectedStoreIds(c.stores.map((s: any) => s.storeConfigId))
                }
            }
        } catch {
            toast.error('Erro ao carregar cupom')
            navigate('/commerce/coupons')
        } finally {
            setLoadingData(false)
        }
    }

    async function handleGenerateCode() {
        setGeneratingCode(true)
        try {
            const response = await generateCouponCode()
            if (response?.success && response.result?.code) {
                setCode(response.result.code)
            }
        } catch {
            toast.error('Erro ao gerar código')
        } finally {
            setGeneratingCode(false)
        }
    }

    function toggleProduct(productId: number) {
        setSelectedProductIds((prev) =>
            prev.includes(productId) ? prev.filter((id) => id !== productId) : [...prev, productId]
        )
    }

    function removeProduct(productId: number) {
        setSelectedProductIds((prev) => prev.filter((id) => id !== productId))
    }

    const filteredProducts = products.filter((p) =>
        p.name.toLowerCase().includes(productSearch.toLowerCase())
    )

    async function handleSubmit(e: React.FormEvent) {
        e.preventDefault()

        if (!code.trim()) {
            toast.error('Código do cupom é obrigatório')
            return
        }
        if (!startDate || !endDate) {
            toast.error('Datas de início e término são obrigatórias')
            return
        }
        if (!discountValue || parseFloat(discountValue) <= 0) {
            toast.error('Valor do desconto deve ser maior que zero')
            return
        }
        if (discountType === 'PERCENTAGE' && parseFloat(discountValue) > 100) {
            toast.error('Desconto percentual não pode ser maior que 100%')
            return
        }

        setLoading(true)
        try {
            const payload: Record<string, any> = {
                code: code.trim().toUpperCase(),
                name: name.trim() || undefined,
                description: description.trim() || undefined,
                discountType,
                discountValue:
                    discountType === 'PERCENTAGE'
                        ? parseFloat(discountValue)
                        : Math.round(parseFloat(discountValue) * 100),
                startDate: new Date(startDate + 'T00:00:00').toISOString(),
                endDate: new Date(endDate + 'T23:59:59').toISOString(),
                maxUses: maxUses ? parseInt(maxUses) : undefined,
                maxUsesPerClient: maxUsesPerClient ? parseInt(maxUsesPerClient) : undefined,
                minimumPurchase: minimumPurchase
                    ? Math.round(parseFloat(minimumPurchase) * 100)
                    : undefined,
                active,
                categoryId: selectedCategoryId || undefined,
                subcategoryId: selectedSubcategoryId || undefined,
                productIds: selectedProductIds.length > 0 ? selectedProductIds : undefined,
                storeConfigIds: selectedStoreIds.length > 0 ? selectedStoreIds : undefined,
                companyId,
            }

            if (isEditing && id) {
                await updateCoupon(parseInt(id), payload)
                toast.success('Cupom atualizado com sucesso')
            } else {
                await createCoupon(payload)
                toast.success('Cupom criado com sucesso')
            }

            navigate('/commerce/coupons')
        } catch (err: any) {
            const msg = err?.response?.data?.message || 'Erro ao salvar cupom'
            toast.error(typeof msg === 'string' ? msg : msg[0])
        } finally {
            setLoading(false)
        }
    }

    if (loadingData) {
        return (
            <div className="flex items-center justify-center min-h-[400px]">
                <span className="loading loading-spinner loading-lg" />
            </div>
        )
    }

    return (
        <div className="max-w-4xl mx-auto p-4 space-y-6">
            {/* Header */}
            <div className="flex items-center gap-3">
                <MdConfirmationNumber className="text-2xl text-secondary" />
                <h1 className="text-2xl font-bold">
                    {isEditing ? 'Editar Cupom' : 'Novo Cupom'}
                </h1>
            </div>

            {/* Usage stats in edit mode */}
            {isEditing && coupon && (
                <div className="stats shadow">
                    <div className="stat">
                        <div className="stat-title">Usos</div>
                        <div className="stat-value text-lg">{coupon.currentUses}</div>
                        <div className="stat-desc">
                            {coupon.maxUses ? `de ${coupon.maxUses}` : 'Ilimitado'}
                        </div>
                    </div>
                </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
                {/* Informações básicas */}
                <div className="bg-base-100 rounded-xl border border-base-300/50 p-6 space-y-4">
                    <h2 className="text-lg font-semibold">Informações do Cupom</h2>

                    {/* Código */}
                    <div className="form-control w-full">
                        <label className="label">
                            <span className="label-text font-medium">
                                Código do Cupom <span className="text-error">*</span>
                            </span>
                        </label>
                        <div className="flex gap-2">
                            <input
                                type="text"
                                className="input input-bordered flex-1 font-mono uppercase"
                                value={code}
                                onChange={(e) => setCode(e.target.value.toUpperCase())}
                                placeholder="Ex: NATAL10"
                            />
                            <button
                                type="button"
                                className="btn btn-outline btn-sm"
                                onClick={handleGenerateCode}
                                disabled={generatingCode}
                            >
                                {generatingCode ? (
                                    <span className="loading loading-spinner loading-xs" />
                                ) : (
                                    <MdAutorenew />
                                )}
                                Gerar
                            </button>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {/* Nome */}
                        <div className="form-control w-full">
                            <label className="label">
                                <span className="label-text font-medium">Nome</span>
                            </label>
                            <input
                                type="text"
                                className="input input-bordered w-full"
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                placeholder="Ex: Desconto de Natal"
                            />
                        </div>

                        {/* Tipo de Desconto */}
                        <div className="form-control w-full">
                            <label className="label">
                                <span className="label-text font-medium">Tipo de Desconto</span>
                            </label>
                            <select
                                className="select select-bordered w-full"
                                value={discountType}
                                onChange={(e) =>
                                    setDiscountType(e.target.value as 'PERCENTAGE' | 'FIXED_AMOUNT')
                                }
                            >
                                <option value="PERCENTAGE">Percentual (%)</option>
                                <option value="FIXED_AMOUNT">Valor Fixo (R$)</option>
                            </select>
                        </div>
                    </div>

                    {/* Valor do Desconto */}
                    <div className="form-control w-full max-w-xs">
                        <label className="label">
                            <span className="label-text font-medium">
                                Valor do Desconto <span className="text-error">*</span>
                            </span>
                        </label>
                        <label className="input input-bordered flex items-center gap-2">
                            {discountType === 'FIXED_AMOUNT' && (
                                <span className="text-base-content/60">R$</span>
                            )}
                            <input
                                type="number"
                                className="grow"
                                value={discountValue}
                                onChange={(e) => setDiscountValue(e.target.value)}
                                placeholder="0"
                                step={discountType === 'PERCENTAGE' ? '1' : '0.01'}
                                min="0"
                                max={discountType === 'PERCENTAGE' ? '100' : undefined}
                            />
                            {discountType === 'PERCENTAGE' && (
                                <span className="text-base-content/60">%</span>
                            )}
                        </label>
                        <label className="label">
                            <span className="label-text-alt text-base-content/50">
                                {discountType === 'PERCENTAGE'
                                    ? 'Percentual de 1 a 100'
                                    : 'Valor em reais (ex: 25.50)'}
                            </span>
                        </label>
                    </div>

                    {/* Descrição */}
                    <div className="form-control w-full">
                        <label className="label">
                            <span className="label-text font-medium">Descrição</span>
                        </label>
                        <textarea
                            className="textarea textarea-bordered w-full"
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            placeholder="Descrição do cupom (opcional)"
                            rows={3}
                        />
                    </div>
                </div>

                {/* Período e limites */}
                <div className="bg-base-100 rounded-xl border border-base-300/50 p-6 space-y-4">
                    <h2 className="text-lg font-semibold">Período e Limites</h2>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {/* Data de Início */}
                        <div className="form-control w-full">
                            <label className="label">
                                <span className="label-text font-medium">
                                    Data de Início <span className="text-error">*</span>
                                </span>
                            </label>
                            <input
                                type="date"
                                className="input input-bordered w-full"
                                value={startDate}
                                onChange={(e) => setStartDate(e.target.value)}
                            />
                        </div>

                        {/* Data de Término */}
                        <div className="form-control w-full">
                            <label className="label">
                                <span className="label-text font-medium">
                                    Data de Término <span className="text-error">*</span>
                                </span>
                            </label>
                            <input
                                type="date"
                                className="input input-bordered w-full"
                                value={endDate}
                                onChange={(e) => setEndDate(e.target.value)}
                            />
                        </div>

                        {/* Máximo de usos totais */}
                        <div className="form-control w-full">
                            <label className="label">
                                <span className="label-text font-medium">
                                    Máximo de usos totais
                                </span>
                            </label>
                            <input
                                type="number"
                                className="input input-bordered w-full"
                                value={maxUses}
                                onChange={(e) => setMaxUses(e.target.value)}
                                placeholder="Ilimitado"
                                min="1"
                            />
                        </div>

                        {/* Máximo de usos por cliente */}
                        <div className="form-control w-full">
                            <label className="label">
                                <span className="label-text font-medium">
                                    Máximo de usos por cliente
                                </span>
                            </label>
                            <input
                                type="number"
                                className="input input-bordered w-full"
                                value={maxUsesPerClient}
                                onChange={(e) => setMaxUsesPerClient(e.target.value)}
                                placeholder="Ilimitado"
                                min="1"
                            />
                        </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {/* Valor mínimo de compra */}
                        <div className="form-control w-full">
                            <label className="label">
                                <span className="label-text font-medium">
                                    Valor mínimo de compra
                                </span>
                            </label>
                            <label className="input input-bordered flex items-center gap-2">
                                <span className="text-base-content/60">R$</span>
                                <input
                                    type="number"
                                    className="grow"
                                    value={minimumPurchase}
                                    onChange={(e) => setMinimumPurchase(e.target.value)}
                                    placeholder="Sem mínimo"
                                    step="0.01"
                                    min="0"
                                />
                            </label>
                        </div>

                        {/* Ativo */}
                        <div className="form-control w-full">
                            <label className="label">
                                <span className="label-text font-medium">Status</span>
                            </label>
                            <label className="label cursor-pointer justify-start gap-3">
                                <input
                                    type="checkbox"
                                    className="toggle toggle-secondary"
                                    checked={active}
                                    onChange={(e) => setActive(e.target.checked)}
                                />
                                <span className="label-text">
                                    {active ? 'Ativo' : 'Inativo'}
                                </span>
                            </label>
                        </div>
                    </div>
                </div>

                {/* Lojas */}
                {availableStores.length > 0 && (
                    <div className="bg-base-100 rounded-xl border border-base-300/50 p-6">
                        <h3 className="text-base font-semibold mb-1 pb-3 border-b border-base-300/50">
                            Lojas
                        </h3>
                        <p className="text-sm text-base-content/60 mb-4">
                            Selecione as lojas onde este cupom será ativado. Deixe vazio para aplicar em todas.
                        </p>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            {availableStores.map((store) => (
                                <label key={store.id} className="flex items-center gap-3 rounded-lg border p-3 cursor-pointer transition-colors hover:bg-base-200/50">
                                    <input
                                        type="checkbox"
                                        className="checkbox checkbox-secondary checkbox-sm"
                                        checked={selectedStoreIds.includes(store.id)}
                                        onChange={(e) => {
                                            if (e.target.checked) {
                                                setSelectedStoreIds([...selectedStoreIds, store.id])
                                            } else {
                                                setSelectedStoreIds(selectedStoreIds.filter((id) => id !== store.id))
                                            }
                                        }}
                                    />
                                    <div className="flex items-center gap-2">
                                        {store.logo && (
                                            <img src={store.logo} alt={store.name} className="w-8 h-8 rounded object-cover" />
                                        )}
                                        <div>
                                            <p className="text-sm font-medium">{store.name}</p>
                                            <p className="text-xs text-base-content/50">{store.subdomain}</p>
                                        </div>
                                    </div>
                                </label>
                            ))}
                        </div>
                    </div>
                )}

                {/* Filtros de aplicação */}
                <div className="bg-base-100 rounded-xl border border-base-300/50 p-6 space-y-4">
                    <div
                        className="flex items-center justify-between cursor-pointer"
                        onClick={() => setShowFilters(!showFilters)}
                    >
                        <div>
                            <h2 className="text-lg font-semibold">
                                Filtros de aplicação (opcional)
                            </h2>
                            <p className="text-sm text-base-content/60">
                                Deixe vazio para aplicar em todos os produtos
                            </p>
                        </div>
                        <button type="button" className="btn btn-ghost btn-sm">
                            {showFilters ? 'Ocultar' : 'Mostrar'}
                        </button>
                    </div>

                    {showFilters && (
                        <div className="space-y-4 pt-2">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                {/* Categoria */}
                                <div className="form-control w-full">
                                    <label className="label">
                                        <span className="label-text font-medium">Categoria</span>
                                    </label>
                                    <select
                                        className="select select-bordered w-full"
                                        value={selectedCategoryId}
                                        onChange={(e) =>
                                            setSelectedCategoryId(
                                                e.target.value ? parseInt(e.target.value) : ''
                                            )
                                        }
                                    >
                                        <option value="">Todas as categorias</option>
                                        {categories.map((cat) => (
                                            <option key={cat.id} value={cat.id}>
                                                {cat.name}
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                {/* Subcategoria */}
                                <div className="form-control w-full">
                                    <label className="label">
                                        <span className="label-text font-medium">
                                            Subcategoria
                                        </span>
                                    </label>
                                    <select
                                        className="select select-bordered w-full"
                                        value={selectedSubcategoryId}
                                        onChange={(e) =>
                                            setSelectedSubcategoryId(
                                                e.target.value ? parseInt(e.target.value) : ''
                                            )
                                        }
                                        disabled={!selectedCategoryId}
                                    >
                                        <option value="">Todas as subcategorias</option>
                                        {subcategories.map((sub) => (
                                            <option key={sub.id} value={sub.id}>
                                                {sub.name}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            </div>

                            {/* Produtos Específicos */}
                            <div className="form-control w-full">
                                <label className="label">
                                    <span className="label-text font-medium">
                                        Produtos Específicos
                                    </span>
                                    {selectedProductIds.length > 0 && (
                                        <span className="label-text-alt badge badge-secondary badge-sm">
                                            {selectedProductIds.length} selecionado
                                            {selectedProductIds.length > 1 ? 's' : ''}
                                        </span>
                                    )}
                                </label>

                                {/* Selected product badges */}
                                {selectedProductIds.length > 0 && (
                                    <div className="flex flex-wrap gap-2 mb-3">
                                        {selectedProductIds.map((pid) => {
                                            const product = products.find((p) => p.id === pid)
                                            if (!product) return null
                                            return (
                                                <span
                                                    key={pid}
                                                    className="badge badge-outline gap-1"
                                                >
                                                    {product.name}
                                                    <button
                                                        type="button"
                                                        className="btn btn-ghost btn-xs p-0"
                                                        onClick={() => removeProduct(pid)}
                                                    >
                                                        x
                                                    </button>
                                                </span>
                                            )
                                        })}
                                    </div>
                                )}

                                {/* Search */}
                                <input
                                    type="text"
                                    className="input input-bordered input-sm w-full mb-2"
                                    placeholder="Buscar produtos..."
                                    value={productSearch}
                                    onChange={(e) => setProductSearch(e.target.value)}
                                />

                                {/* Product checkbox list */}
                                <div className="max-h-60 overflow-y-auto border border-base-300 rounded-lg p-2 space-y-1">
                                    {filteredProducts.length === 0 ? (
                                        <p className="text-sm text-base-content/50 text-center py-4">
                                            Nenhum produto encontrado
                                        </p>
                                    ) : (
                                        filteredProducts.map((product) => (
                                            <label
                                                key={product.id}
                                                className="flex items-center gap-3 p-2 rounded-lg hover:bg-base-200 cursor-pointer"
                                            >
                                                <input
                                                    type="checkbox"
                                                    className="checkbox checkbox-sm checkbox-secondary"
                                                    checked={selectedProductIds.includes(
                                                        product.id
                                                    )}
                                                    onChange={() => toggleProduct(product.id)}
                                                />
                                                {product.photo && (
                                                    <img
                                                        src={product.photo}
                                                        alt={product.name}
                                                        className="w-8 h-8 rounded object-cover"
                                                    />
                                                )}
                                                <span className="text-sm flex-1">
                                                    {product.name}
                                                </span>
                                                <span className="text-xs text-base-content/50">
                                                    R${' '}
                                                    {(product.price / 100)
                                                        .toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                                                        .replace('.', ',')}
                                                </span>
                                            </label>
                                        ))
                                    )}
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                {/* Actions */}
                <div className="flex justify-end gap-3">
                    <button
                        type="button"
                        className="btn btn-ghost"
                        onClick={() => navigate('/commerce/coupons')}
                    >
                        Cancelar
                    </button>
                    <button type="submit" className="btn btn-secondary" disabled={loading}>
                        {loading && <span className="loading loading-spinner loading-xs" />}
                        {isEditing ? 'Salvar Alterações' : 'Criar Cupom'}
                    </button>
                </div>
            </form>
        </div>
    )
}
